<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue';

interface Props {
  greetingMessage?: string;
  customMessage?: string;
  fixedMessage?: string;
  closeMessage?: string;
  directInvitation?: boolean;
  maxCharacters?: number;
}

interface Emits {
  (e: 'greetingMessageChange', value: string): void;
  (e: 'customMessageChange', value: string): void;
  (e: 'fixedMessageChange', value: string): void;
  (e: 'closeMessageChange', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  directInvitation: true,
});

const emit = defineEmits<Emits>();

const customMessageText = ref('');
const fixedMessageText = ref('');
const closeMessageText = ref('');
const greetingMessageText = ref('');

const textareaSize = ref<HTMLTextAreaElement>();
const textareaSize2 = ref<HTMLTextAreaElement>();
const textareaSize3 = ref<HTMLTextAreaElement>();

const characterCountWithinLimit = computed(() => {
  return customMessageText.value.length < (props.maxCharacters || Infinity);
});

const getCharacterCounterStyle = computed(() => {
  if (!characterCountWithinLimit.value) return { color: 'red' };
  return {};
});

const resizeTextarea = () => {
  if (textareaSize.value) {
    textareaSize.value.style.height = textareaSize.value.scrollHeight + 'px';
  }
};

onMounted(async () => {
  customMessageText.value = props.customMessage || '';
  fixedMessageText.value = props.fixedMessage || '';
  closeMessageText.value = props.closeMessage || '';
  greetingMessageText.value = props.greetingMessage || '';

  await nextTick();
  setTimeout(() => {
    if (textareaSize.value) {
      textareaSize.value.style.height = textareaSize.value.scrollHeight + 'px';
    }
    if (textareaSize2.value) {
      textareaSize2.value.style.height = textareaSize2.value.scrollHeight + 'px';
    }
  }, 50);
});

watch(
  () => props.customMessage,
  newVal => {
    customMessageText.value = newVal || '';
  }
);

watch(
  () => props.fixedMessage,
  newVal => {
    fixedMessageText.value = newVal || '';
  }
);

watch(
  () => props.closeMessage,
  newVal => {
    closeMessageText.value = newVal || '';
  }
);

watch(
  () => props.greetingMessage,
  newVal => {
    greetingMessageText.value = newVal || '';
  }
);
</script>

<template>
  <div class="uk-flex uk-flex-column uk-margin-large-bottom" style="color: black">
    <div class="custom-scroll-box">
      <div
        v-if="directInvitation"
        class="uk-textarea input-field textarea2"
        v-html="greetingMessageText"
        ref="textareaSize3"
        @change="emit('greetingMessageChange', greetingMessageText)"
        required
        readonly
      ></div>
      <textarea
        @input="resizeTextarea"
        ref="textareaSize"
        :maxlength="maxCharacters && maxCharacters"
        class="uk-textarea input-field textarea1"
        v-model="customMessageText"
        placeholder="Add a message to the invitation"
        @change="emit('customMessageChange', customMessageText)"
        required
      ></textarea>
      <div
        class="uk-textarea input-field textarea2"
        v-html="fixedMessageText"
        placeholder="Add a message to the invitation"
        ref="textareaSize2"
        @change="emit('fixedMessageChange', fixedMessageText)"
        required
        readonly
      ></div>
      <div
        class="uk-textarea input-field textarea2"
        v-html="closeMessageText"
        placeholder="Add a message to the invitation"
        ref="textareaSize2"
        @change="emit('closeMessageChange', closeMessageText)"
        required
        readonly
      ></div>
    </div>
    <div class="character-count uk-text-right uk-text-small" v-if="maxCharacters">
      Characters left:
      <span :style="getCharacterCounterStyle">{{ maxCharacters - customMessageText.length }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.input-field {
  border: 2px solid #959595;
  font-size: $global-small-font-size;
}
.textarea1 {
  border: none;
  overflow: hidden;
  line-height: 1.4;
}
.textarea2 {
  border: none;
  overflow: hidden;
  background: #f4f4f4;
  color: black;
  line-height: 1.4;
}

.custom-scroll-box {
  max-height: 24vh;
  overflow-y: scroll;
  border: 2px solid #959595;
  border-radius: 5px;
}
</style>

<style lang="scss">
.error {
  input {
    border-color: red;
  }
}
.acc-dec-link {
  text-decoration: underline;
  color: #7cb6c9;
}
</style>
